import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { withViewport } from '../../util/uiHelpers';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { useConfiguration } from '../../context/configurationContext.js';

import { LayoutSingleColumn, NamedLink, NamedRedirect } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import StaticPage from '../PageBuilder/StaticPage.js';

import CommonSection from './CommonSection/CommonSection.js';
import SectionStats from './SectionStats/SectionStats.js';
import SectionListings from './SectionListings/SectionListings.js';
import SectionMission from './SectionMission.js';
import SectionTestimonials from './SectionTestimonials/SectionTestimonials.js';
import SectionCategories from './SectionCategories/SectionCategories.js';
import SectionTeam from './SectionTeam/SectionTeam.js';

import css from './ModernLandingPage.module.css';

const ModernLandingPageComponent = (props) => {
    const {
        queryListingsInProgress,
        queryListingsError,
        listings,
        budgetDetails,
        getBudgetInProgress,
        getBudgetError,
        currentUser,
        intl,
        viewport,
    } = props;

    const config = useConfiguration();
    const isLoggedIn = !!currentUser;

    if (isLoggedIn) {
        return <NamedRedirect name="MyHubPage" />
    };

    const isCarsVariant = typeof window !== "undefined" && location?.pathname == "/cars";
    const isRealEstateVariant = typeof window !== "undefined" && location?.pathname == "/re";

    const sellListings = listings.filter(l => l.attributes.publicData.listingType == "sell");
    const searchListings = listings.filter(l => l.attributes.publicData.listingType == "search");

    const pageMetaProps = {
        "title": intl.formatMessage({ id: 'MLP.meta.title' }),
        "description": intl.formatMessage({ id: 'General.meta.text' }),
        "schema": {
            "@context": "http://schema.org",
            "@type": "Article",
            "description": intl.formatMessage({ id: 'General.meta.text' }),
            "name": intl.formatMessage({ id: 'MLP.meta.title' }),
            "headline": intl.formatMessage({ id: 'MLP.meta.title' }),
        },
        "socialSharing": {
            "title": intl.formatMessage({ id: 'MLP.meta.title' }),
            "description": intl.formatMessage({ id: 'General.meta.text' }),
            "images1200": null,
            "images600": null
        },
    };

    return <StaticPage {...pageMetaProps}>
        <LayoutSingleColumn
            topbar={<TopbarContainer currentPage="LandingPage" />}
            footer={<FooterContainer />}
        >
            <div as="main" className={css.main}>
                <div className={classNames(
                    css.hero,
                    {
                        [css.heroCars]: isCarsVariant,
                        [css.heroRealEstate]: isRealEstateVariant
                    },
                )}>
                    <h1 className={css.heroHeader}>
                        <FormattedMessage id="MLP.hero.title" />
                    </h1>
                    <div className={css.heroButtons}>
                        <NamedLink name="SearchPage" className={css.whiteLink}>
                            <FormattedMessage id="MLP.hero.button" />
                        </NamedLink>
                    </div>
                    <p className={css.heroText}>
                        <FormattedMessage id="MLP.hero.text1" />
                        <span className={css.textSpacer}>|</span>
                        <FormattedMessage id="MLP.hero.text2" />
                        <span className={css.textSpacer}>|</span>
                        <FormattedMessage id="MLP.hero.text3" />
                        <span className={css.textSpacer}>|</span>
                        <FormattedMessage id="MLP.hero.text4" />
                    </p>
                </div>

                <div className={css.content}>
                    <SectionStats
                        budgetDetails={budgetDetails}
                        getBudgetInProgress={getBudgetInProgress}
                        getBudgetError={getBudgetError}
                    />

                    <CommonSection
                        title={intl.formatMessage({ id: 'MLP.listings.title' })}
                        text={intl.formatMessage({ id: 'MLP.listings.text' })}
                        linkText={intl.formatMessage({ id: 'MLP.listings.button' })}
                        linkName="SearchPage"
                        className={css.commonSection}
                    />
                </div>

                <SectionListings
                    queryListingsInProgress={queryListingsInProgress}
                    queryListingsError={queryListingsError}
                    sellListings={sellListings}
                    searchListings={searchListings}
                    config={config}
                    currentUser={currentUser}
                    viewport={viewport}
                />

                <div className={css.content}>
                    <CommonSection
                        title={intl.formatMessage({ id: 'MLP.createListing.title' })}
                        text={intl.formatMessage({ id: 'MLP.createListing.text' })}
                        linkText={intl.formatMessage({ id: 'MLP.createListing.button' })}
                        linkName="NewListingPage"
                        className={css.commonSection}
                        hideLink={true}
                    />

                    <SectionCategories
                        config={config}
                        isCarsVariant={isCarsVariant}
                        isRealEstateVariant={isRealEstateVariant}
                    />

                    <SectionMission />

                    <CommonSection
                        title={intl.formatMessage({ id: 'MLP.membership.title' })}
                        text={intl.formatMessage({ id: 'MLP.membership.text' })}
                        linkText={intl.formatMessage({ id: 'MLP.membership.button' })}
                        linkName="ContactPage"
                        linkTo={{ search: "?variant=apply" }}
                        className={css.membersSection}
                    />

                    <SectionTestimonials intl={intl} />

                    <CommonSection
                        title={intl.formatMessage({ id: 'MLP.finance.title' })}
                        text={intl.formatMessage({ id: 'MLP.finance.text' })}
                        linkText={intl.formatMessage({ id: 'MLP.finance.button' })}
                        linkName="DebtPage"
                        className={css.financeSection}
                    />

                    <SectionTeam />
                </div>
                <div>
                    {isCarsVariant ? <CommonSection
                        title={intl.formatMessage({ id: 'MLP.getStarted.cars.title' })}
                        linkText={intl.formatMessage({ id: 'MLP.getStarted.cars.button' })}
                        linkName="CMSPage"
                        linkParams={{ pageId: "contact" }}
                        className={css.getStartedCars}
                    /> : null}

                    {isRealEstateVariant ? <CommonSection
                        title={intl.formatMessage({ id: 'MLP.getStarted.realEstate.title' })}
                        linkText={intl.formatMessage({ id: 'MLP.getStarted.realEstate.button' })}
                        linkName="CMSPage"
                        linkParams={{ pageId: "contact" }}
                        className={css.getStartedRealEstate}
                    /> : null}
                </div>
            </div>
        </LayoutSingleColumn>
    </StaticPage>
};

const mapStateToProps = state => {
    const { currentUser } = state.user;
    const {
        listingRefs,
        queryListingsInProgress,
        queryListingsError,
        budgetDetails,
        getBudgetInProgress,
        getBudgetError,
    } = state.ModernLandingPage;

    const listings = getMarketplaceEntities(state, listingRefs);

    return {
        queryListingsInProgress,
        queryListingsError,
        listings,
        budgetDetails,
        getBudgetInProgress,
        getBudgetError,
        currentUser,
    };
};

const ModernLandingPage = compose(
    connect(mapStateToProps),
    injectIntl,
    withViewport,
)(ModernLandingPageComponent);

export default ModernLandingPage;
